import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import { useRouter } from 'next/router';
import { useCookies } from 'react-cookie';
import { useEffect } from 'react';
import stringUtils from '../utils/stringUtils';
/*
 * Local JSON configs are manual snapshots of DataRouter flag values per environment.
 * They do NOT sync automatically — update the JSON files when:
 *   1. You need to test a new flag locally
 *   2. DataRouter values change and local dev should reflect the update
 */
import qaConfig from '../../config/local-frontend-config.qa.json';
import prodConfig from '../../config/local-frontend-config.prod.json';

const COOKIE_PREFIX = 'fc_override_';
const RESET_ALL_PARAM = 'fc_reset_all';
const COOKIE_MAX_AGE = 86400; // 24 hours in seconds
const LOCAL_ENV = process.env.NEXT_PUBLIC_LOCAL_ENV;
const loggedOverrides = new Set<string>();

const LOCAL_CONFIGS: Record<string, Record<string, boolean>> = {
  qa: qaConfig,
  prod: prodConfig,
};

function getLocalConfig(key: string): boolean | undefined {
  const config = LOCAL_ENV ? LOCAL_CONFIGS[LOCAL_ENV] : undefined;
  if (config && key in config) return Boolean(config[key]);
  return undefined;
}

/**
 * Hook for reading boolean feature flags with override support.
 *
 * Priority chain:
 * 1. Query param (one-off, auto-persisted as cookie)
 * 2. Cookie (override, auto-expires after 24 hours)
 * 3. Local JSON config (localhost only, based on NEXT_PUBLIC_LOCAL_ENV)
 * 4. DataRouter config (QA/prod)
 * 5. Hardcoded default
 *
 * Override via query param: ?enableFeatureFlag=true or ?enableFeatureFlag=false
 * Clear override: ?enableFeatureFlag=reset
 */
export default function useBooleanConfig(key: string, defaultValue: boolean): boolean {
  const router = useRouter();
  const cookieName = `${COOKIE_PREFIX}${key}`;
  const [cookies, setCookie, removeCookie] = useCookies([cookieName]);

  const queryParamValue = router.query[key]?.toString();
  const resetAllValue = router.query[RESET_ALL_PARAM]?.toString();

  // Clear all cookie overrides when ?fc_reset_all=true
  useEffect(() => {
    if (resetAllValue !== 'true') return;
    const overrideKeys = Object.keys(cookies).filter((name) => name.startsWith(COOKIE_PREFIX));
    overrideKeys.forEach((name) => {
      removeCookie(name, { path: '/' });
    });
    if (LOCAL_ENV && overrideKeys.length > 0 && !loggedOverrides.has(RESET_ALL_PARAM)) {
      loggedOverrides.add(RESET_ALL_PARAM);
      // eslint-disable-next-line no-console
      console.info(`[useBooleanConfig] Cleared ${overrideKeys.length} cookie override(s) via ?${RESET_ALL_PARAM}=true`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetAllValue]);

  // Auto-persist query param as cookie
  useEffect(() => {
    if (!queryParamValue) return;
    if (queryParamValue === 'reset') {
      removeCookie(cookieName, { path: '/' });
    } else if (stringUtils.isTrue(queryParamValue) || stringUtils.isFalse(queryParamValue)) {
      setCookie(cookieName, queryParamValue, { path: '/', maxAge: COOKIE_MAX_AGE });
    }
  }, [queryParamValue, cookieName, setCookie, removeCookie]);

  // 1. Query param
  if (queryParamValue && queryParamValue !== 'reset') {
    if (stringUtils.isTrue(queryParamValue)) return true;
    if (stringUtils.isFalse(queryParamValue)) return false;
  }

  // 2. Cookie override
  const cookieValue = cookies[cookieName];
  if (cookieValue !== undefined) {
    if (LOCAL_ENV && !loggedOverrides.has(cookieName)) {
      loggedOverrides.add(cookieName);
      // eslint-disable-next-line no-console
      console.info(
        `[useBooleanConfig] Cookie override active: ${cookieName} = ${cookieValue} (expires in 24h, clear with ?${key}=reset)`,
      );
    }
    if (stringUtils.isTrue(String(cookieValue))) return true;
    if (stringUtils.isFalse(String(cookieValue))) return false;
  }

  // 3. Local JSON config (localhost) or 4. DataRouter config (QA/prod)
  const localValue = getLocalConfig(key);
  if (localValue !== undefined) return localValue;

  return getBooleanConfig(key, defaultValue);
}
